import styled, { css } from 'styled-components';
import { DeprecatedGrid as Grid } from '@simplywallst/ui-core';
import {
  HEADING_CLASS_NAME,
  PARAGRAPGH_CLASS_NAME,
  BOLD_CLASS_NAME,
  UNORDERED_LIST_CLASS_NAME,
  ORDERED_LIST_CLASS_NAME,
  LIST_ITEM_CLASS_NAME,
  IMAGE_CLASS_NAME,
} from './constant';

const color = css`
  color: ${({ theme }) => theme.color.white};
`;

const firstChild = css`
  &:first-child {
    margin-top: 0;
  }
`;

const heading = css`
  ${color}
  ${firstChild}
  margin-top: ${({ theme }) => theme.x2spacePx}px;
  margin-bottom: 0;
`;

const h3 = css`
  ${({ theme }) => theme.typography.editorHeading}
  ${heading}
`;

const h4 = css`
  ${({ theme }) => theme.typography.editorSubHeading}
  ${heading}
`;

const h5 = css`
  ${({ theme }) => theme.typography.editorBodyStrong}
  ${heading}
`;

const paragraph = css`
  ${({ theme }) => theme.typography.editorBody}
  ${color}
  ${firstChild}
  margin-top: ${({ theme }) => theme.x2spacePx - 4}px;
  margin-bottom: 0;
`;

const bold = css`
  ${({ theme }) => theme.typography.editorBodyStrong}
`;

const list = css`
  ${firstChild}
  margin-top: ${({ theme }) => theme.x2spacePx - 4}px;
  margin-bottom: 0;
`;

const ul = css`
  ${list}
  li {
    list-style-type: disc;
  }
`;

const ol = css`
  ${list}
  li {
    list-style-type: decimal;
  }
`;

const li = css`
  ${color}
  padding-left: ${({ theme }) => theme.x1spacePx / 2}px;
  margin-left: ${({ theme }) => theme.x3spacePx}px;
  margin-top: ${({ theme }) => theme.x1spacePx}px;
  margin-bottom: 0;
`;

export const StyledRichTextEditorContent = styled('section')`
  h3.${HEADING_CLASS_NAME} {
    ${h3}
  }

  h4.${HEADING_CLASS_NAME} {
    ${h4}
  }

  h5.${HEADING_CLASS_NAME} {
    ${h5}
  }

  .${PARAGRAPGH_CLASS_NAME} {
    ${paragraph}
  }

  .${BOLD_CLASS_NAME} {
    ${bold}
  }

  .${UNORDERED_LIST_CLASS_NAME} {
    ${ul}
  }

  .${ORDERED_LIST_CLASS_NAME} {
    ${ol}
  }

  .${LIST_ITEM_CLASS_NAME} {
    ${li}
  }

  .${IMAGE_CLASS_NAME} {
    width: auto;
    object-fit: contain;
  }

  .is-editor-empty {
    &:before {
      color: ${({ theme }) => theme.color.mediumGray};
      content: attr(data-placeholder);
      float: left;
      height: 0;
      pointer-events: none;
    }
  }
`;

export const StyledGridFooter = styled(Grid)`
  border-top: 1px solid ${({ theme }) => theme.color.whiteRGBA(0.2)};
  padding-top: ${({ theme }) => theme.x1spacePx}px;
`;

export const StyledIndicator = styled('div')`
  transform: rotate(-90deg);
`;

const circleCSS = css`
  stroke-width: 2px;
  stroke-linecap: round;
  r: 9;
  fill: none;
  cx: 50%;
  cy: 50%;
`;

export const StyledBackgroundCircle = styled('circle')`
  stroke: ${({ theme }) => theme.color.whiteRGBA(0.1)};
  ${circleCSS}
`;

export type IndicatorStatus = 'good' | 'warning' | 'complete';

export const StyledIndicatorCircle = styled('circle')<{
  $status: IndicatorStatus;
  $strokeDashoffset: number;
  $strokeDasharray: number;
}>`
  stroke: ${({ theme, $status }) => {
    if ($status === 'warning') return theme.color.neutral;
    if ($status === 'complete') return theme.color.bad;
    return theme.color.brand01;
  }};
  stroke-dashoffset: ${({ $strokeDashoffset }) => $strokeDashoffset};
  stroke-dasharray: ${({ $strokeDasharray }) => $strokeDasharray};
  transition:
    stroke-dashoffset 0.2s,
    stroke 0.4s;
  ${circleCSS}
`;

export const TogglesWrapper = styled(Grid)`
  grid-auto-flow: column;
  grid-auto-columns: max-content;
  min-height: ${({ theme }) => theme.x5spacePx}px;
`;
